import React, { useCallback, useEffect, useState } from 'react';
import medicalPrescriptionService from '../../services/medicalPrescriptionService';
import pharmacyService from '../../services/pharmacyService';
import SiteService from '../../services/siteService';
import ConsultationPrescriptionViewModal from './ConsultationPrescriptionViewModal';
import RxPrescriptionStockWarningModal from './RxPrescriptionStockWarningModal';
import RxMedicationSearch from './RxMedicationSearch';
import Icon from '../common/Icons';
import { ClinicalButton, ClinicalButtonStack } from './ui';
import { formatQuantitySupply } from './utils/prescriptionRedaction';
import {
  catalogFromPharmacyStockRows,
  getPrescriptionStockWarning,
  mergeDualLocationStockRows,
  resolveMedicationStockRow,
  stockMapFromItems
} from './utils/medicationStockDisplay';
import { formatClinicalRefNumber } from './utils/prescriptionRedaction';
import { canEditPrescription } from './utils/prescriptionPharmacyEditability';
import { siteHasDutyCabinet, STOCK_LOCATIONS } from '../../constants/siteCareModes';
import './ConsultationPrescriptionPanel.css';

const POSOLOGY_SNIPPETS = [
  '1 cp matin, midi et soir',
  '1 cp matin et soir',
  '1 cp le soir',
  '1 cp x 2 / j',
  '1 sachet x 1 / j',
  'pendant 5 jours',
  'pendant 7 jours'
];

const emptyDraft = () => ({
  medication_id: '',
  quantity: '1',
  dosage: ''
});

function itemsToLines(items, medications = []) {
  return (items || []).map((item) => {
    const med = medications.find((m) => String(m.id) === String(item.medication_id));
    return {
      medication_id: String(item.medication_id),
      medication_name: item.medication_name || med?.name || '',
      unit_name: med?.unit_name || item.unit_name || '',
      quantity: item.quantity,
      dosage: item.dosage || ''
    };
  });
}

export default function ConsultationPrescriptionPanel({
  consultation,
  selectedSiteId = null,
  selectedSiteName = '',
  readOnly = false,
  /** Si défini, hydrate cette ordonnance (liste Ordonnances / multi-rx). */
  focusPrescriptionId = null,
  onPrescriptionSuccess
}) {
  const [medications, setMedications] = useState([]);
  const [medInput, setMedInput] = useState('');
  const [selectedMed, setSelectedMed] = useState(null);
  const [draft, setDraft] = useState(emptyDraft);
  const [lines, setLines] = useState([]);
  const [notes, setNotes] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [savedPrescriptions, setSavedPrescriptions] = useState([]);
  const [activePrescriptionId, setActivePrescriptionId] = useState(null);
  const [loadingSaved, setLoadingSaved] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewPrescription, setViewPrescription] = useState(null);
  const [viewPreviewUnsaved, setViewPreviewUnsaved] = useState(false);
  const [loadingView, setLoadingView] = useState(false);
  const [stockByMedicationId, setStockByMedicationId] = useState({});
  const [stockSiteLabel, setStockSiteLabel] = useState('');
  const [loadingStock, setLoadingStock] = useState(false);
  const [stockLoadError, setStockLoadError] = useState('');
  const [stockWarningPrompt, setStockWarningPrompt] = useState(null);
  const [pharmacyTransfer, setPharmacyTransfer] = useState(null);

  const cid = consultation?.id;
  const stockReady = Boolean(stockSiteLabel) && !loadingStock;
  const focusId = focusPrescriptionId ? String(focusPrescriptionId) : null;

  const hydrateFromPrescription = useCallback((prescription) => {
    if (!prescription?.items?.length) {
      setActivePrescriptionId(prescription?.id || null);
      setLines([]);
      setNotes(prescription?.notes || '');
      return;
    }
    setActivePrescriptionId(prescription.id);
    setLines(itemsToLines(prescription.items));
    setNotes(prescription.notes || '');
  }, []);

  /** Stock site — MAIN (+ DUTY si armoire de garde) pour aide à la prescription. */
  const loadSiteStock = useCallback(async () => {
    if (!selectedSiteId) {
      setMedications([]);
      setStockByMedicationId({});
      setStockSiteLabel('');
      setStockLoadError('');
      return;
    }
    setLoadingStock(true);
    setStockLoadError('');
    try {
      let dualLocations = false;
      try {
        const siteRes = await SiteService.getSiteById(selectedSiteId);
        const site = siteRes?.success && siteRes?.data ? siteRes.data : null;
        dualLocations = siteHasDutyCabinet(site);
      } catch (siteErr) {
        console.warn('Impossible de vérifier l’armoire de garde:', siteErr);
      }

      const stockParams = { site_inventory_only: 'true' };
      let rows = [];
      let ok = false;
      let failMessage = '';

      if (dualLocations) {
        const [mainRes, dutyRes] = await Promise.all([
          pharmacyService.getSiteStock(selectedSiteId, {
            ...stockParams,
            stock_location: STOCK_LOCATIONS.MAIN
          }),
          pharmacyService.getSiteStock(selectedSiteId, {
            ...stockParams,
            stock_location: STOCK_LOCATIONS.DUTY
          })
        ]);
        ok = Boolean(mainRes?.success) && Boolean(dutyRes?.success);
        if (ok) {
          rows = mergeDualLocationStockRows(
            Array.isArray(mainRes.data) ? mainRes.data : [],
            Array.isArray(dutyRes.data) ? dutyRes.data : []
          );
        } else {
          failMessage =
            (!mainRes?.success && mainRes?.message) ||
            (!dutyRes?.success && dutyRes?.message) ||
            'Stock site indisponible.';
        }
      } else {
        const res = await pharmacyService.getSiteStock(selectedSiteId, {
          ...stockParams,
          stock_location: STOCK_LOCATIONS.MAIN
        });
        ok = Boolean(res?.success) && Array.isArray(res.data);
        if (ok) {
          rows = res.data.map((row) => ({
            ...row,
            stock_location: row.stock_location || STOCK_LOCATIONS.MAIN
          }));
        } else {
          failMessage = res?.message || 'Stock site indisponible.';
        }
      }

      if (ok) {
        setStockByMedicationId(stockMapFromItems(rows));
        setMedications(catalogFromPharmacyStockRows(rows, { dualLocations }));
        const siteLabel = selectedSiteName
          ? `Stock · ${selectedSiteName}`
          : `Stock · site #${selectedSiteId}`;
        setStockSiteLabel(
          dualLocations ? `${siteLabel} · pharmacie & armoire de garde` : siteLabel
        );
      } else {
        setMedications([]);
        setStockByMedicationId({});
        setStockSiteLabel('');
        setStockLoadError(failMessage);
      }
    } catch (err) {
      console.error('Erreur chargement stock site:', err);
      setMedications([]);
      setStockByMedicationId({});
      setStockSiteLabel('');
      setStockLoadError(err.message || 'Impossible de charger le stock du site.');
    } finally {
      setLoadingStock(false);
    }
  }, [selectedSiteId, selectedSiteName]);

  const loadSavedPrescriptions = useCallback(async ({ hydrateEditor = false } = {}) => {
    setLoadingSaved(true);
    try {
      let list = [];
      if (cid) {
        const res = await medicalPrescriptionService.getPrescriptionsForConsultation(cid);
        list = res.success && Array.isArray(res.data) ? res.data : [];
      } else if (focusId) {
        const res = await medicalPrescriptionService.getPrescriptionById(focusId);
        if (res.success && res.data) list = [res.data];
      } else {
        setSavedPrescriptions([]);
        if (hydrateEditor) {
          setActivePrescriptionId(null);
          setLines([]);
          setNotes('');
        }
        return [];
      }

      setSavedPrescriptions(list);
      if (hydrateEditor) {
        const focused = focusId
          ? list.find((p) => String(p.id) === focusId) || list[0] || null
          : list[0] || null;
        hydrateFromPrescription(focused);
      }
      return list;
    } catch {
      setSavedPrescriptions([]);
      if (hydrateEditor) {
        setActivePrescriptionId(null);
        setLines([]);
        setNotes('');
      }
      return [];
    } finally {
      setLoadingSaved(false);
    }
  }, [cid, focusId, hydrateFromPrescription]);

  useEffect(() => {
    if (!cid && !focusId) return;
    setDraft(emptyDraft());
    setMedInput('');
    setSelectedMed(null);
    setError('');
    setActivePrescriptionId(null);
    setLines([]);
    setNotes('');
    setSavedPrescriptions([]);
    loadSavedPrescriptions({ hydrateEditor: true });
  }, [cid, focusId, loadSavedPrescriptions]);

  useEffect(() => {
    loadSiteStock();
  }, [selectedSiteId, loadSiteStock]);

  const loadPharmacyTransferStatus = useCallback(async (prescriptionId) => {
    if (!prescriptionId) {
      setPharmacyTransfer(null);
      return;
    }
    try {
      const res = await medicalPrescriptionService.getPharmacyTransferStatus(prescriptionId);
      if (res.success && res.data?.dispensation_id) {
        setPharmacyTransfer(res.data);
      } else {
        setPharmacyTransfer(null);
      }
    } catch {
      setPharmacyTransfer(null);
    }
  }, []);

  useEffect(() => {
    loadPharmacyTransferStatus(activePrescriptionId);
  }, [activePrescriptionId, loadPharmacyTransferStatus]);

  useEffect(() => {
    if (medications.length === 0) return;
    setLines((prev) => {
      if (prev.length === 0) return prev;
      let changed = false;
      const next = prev.map((line) => {
        if (line.unit_name) return line;
        const med = medications.find((m) => String(m.id) === String(line.medication_id));
        if (!med?.unit_name) return line;
        changed = true;
        return {
          ...line,
          unit_name: med.unit_name,
          medication_name: line.medication_name || med.base_name || med.name || ''
        };
      });
      return changed ? next : prev;
    });
  }, [medications]);

  const quantityLabel = selectedMed?.unit_name
    ? `Quantité (${selectedMed.unit_name})`
    : 'Quantité';

  const resetComposer = () => {
    setDraft(emptyDraft());
    setMedInput('');
    setSelectedMed(null);
  };

  const handleMedInputChange = (value) => {
    setMedInput(value);
    const exact = medications.find((m) => (m.name || '') === value.trim());
    if (exact) {
      setSelectedMed(exact);
      setDraft((d) => ({ ...d, medication_id: String(exact.id) }));
    } else {
      setSelectedMed(null);
      setDraft((d) => ({ ...d, medication_id: '' }));
    }
  };

  const handleSelectMedication = (med) => {
    setSelectedMed(med);
    setMedInput(med.name || '');
    setDraft((d) => ({ ...d, medication_id: String(med.id) }));
  };

  const commitAppendLine = (med, qty, poso) => {
    setLines((prev) => [
      ...prev,
      {
        medication_id: String(med.id),
        medication_name: med.base_name || med.name || '',
        unit_name: med.unit_name || '',
        quantity: qty,
        dosage: poso
      }
    ]);
    resetComposer();
    setStockWarningPrompt(null);
  };

  const appendLine = () => {
    setError('');
    const med = selectedMed;
    if (!med) {
      setError('Sélectionnez un médicament dans la liste.');
      return;
    }
    const qty = parseInt(draft.quantity, 10);
    if (!Number.isInteger(qty) || qty < 1) {
      setError('Quantité invalide (entier ≥ 1).');
      return;
    }
    const poso = draft.dosage.trim();
    if (!poso) {
      setError('Indiquez la posologie.');
      return;
    }

    const stockRow = resolveMedicationStockRow(stockByMedicationId, med, stockReady);
    const warning = getPrescriptionStockWarning(stockRow, { requestedQty: qty, med });
    if (warning) {
      setStockWarningPrompt({ med, qty, poso, warning });
      return;
    }

    commitAppendLine(med, qty, poso);
  };

  const handleConfirmPrescribeDespiteStock = () => {
    if (!stockWarningPrompt) return;
    const { med, qty, poso } = stockWarningPrompt;
    commitAppendLine(med, qty, poso);
  };

  const handlePosoKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      appendLine();
    }
  };

  const applySnippet = (text) => {
    setDraft((d) => ({ ...d, dosage: text }));
  };

  const removeLine = (index) => {
    setLines((prev) => prev.filter((_, i) => i !== index));
  };

  const hasDraftLines = lines.length > 0;
  const latestSaved =
    (activePrescriptionId
      ? savedPrescriptions.find((p) => String(p.id) === String(activePrescriptionId))
      : null) ||
    savedPrescriptions[0] ||
    null;
  const pharmacyLocked = !canEditPrescription(latestSaved, pharmacyTransfer);
  const effectiveReadOnly = readOnly || (Boolean(activePrescriptionId || latestSaved) && pharmacyLocked);
  const canView = hasDraftLines || (latestSaved?.items?.length > 0);

  const handleOpenView = async () => {
    setError('');
    const resolvedId = activePrescriptionId || latestSaved?.id || focusId || null;

    if (hasDraftLines) {
      setViewPrescription({
        id: resolvedId || undefined,
        items: lines.map((line) => ({
          medication_id: line.medication_id,
          medication_name: line.medication_name,
          unit_name: line.unit_name,
          quantity: line.quantity,
          dosage: line.dosage
        })),
        notes: notes.trim() || null,
        patient_name: consultation?.patient_name,
        patient_phone: consultation?.patient_phone,
        consultation_id: consultation?.id || latestSaved?.consultation_id || null,
        prescribed_date:
          latestSaved?.prescribed_date || consultation?.consultation_date || new Date().toISOString(),
        site_name: selectedSiteName || consultation?.site_name || latestSaved?.site_name,
        doctor_name: consultation?.doctor_name || latestSaved?.doctor_name
      });
      setViewPreviewUnsaved(!resolvedId);
      setShowViewModal(true);
      return;
    }

    setLoadingView(true);
    try {
      if (cid) {
        const res = await medicalPrescriptionService.getPrescriptionsForConsultation(cid);
        const list = res.success && Array.isArray(res.data) ? res.data : [];
        setSavedPrescriptions(list);
        const latest = focusId
          ? list.find((p) => String(p.id) === focusId) || list[0]
          : resolvedId
            ? list.find((p) => String(p.id) === String(resolvedId)) || list[0]
            : list[0];
        if (!latest?.items?.length) {
          setError('Aucune ordonnance enregistrée.');
          return;
        }
        setViewPrescription(latest);
      } else if (resolvedId) {
        const res = await medicalPrescriptionService.getPrescriptionById(resolvedId);
        if (!res.success || !res.data?.items?.length) {
          setError('Aucune ordonnance enregistrée.');
          return;
        }
        setViewPrescription(res.data);
      } else {
        setError('Aucune ordonnance enregistrée.');
        return;
      }
      setViewPreviewUnsaved(false);
      setShowViewModal(true);
    } catch (e) {
      setError(e.message || 'Impossible d\'ouvrir l\'aperçu.');
    } finally {
      setLoadingView(false);
    }
  };

  const buildPayload = useCallback(
    (c) => {
      const name = (c?.patient_name || '').trim();
      if (name.length < 2) {
        throw new Error('Nom patient incomplet — complétez le dossier clinique.');
      }
      if (lines.length === 0) {
        throw new Error('Ajoutez au moins un médicament.');
      }
      return {
        patient_name: name,
        patient_phone: c?.patient_phone ? String(c.patient_phone).trim() : null,
        consultation_id: c?.id || null,
        site_id: selectedSiteId || c?.site_id || null,
        notes: notes.trim() || null,
        items: lines.map((item) => ({
          medication_id: parseInt(item.medication_id, 10),
          quantity: item.quantity,
          dosage: item.dosage || null,
          duration: null,
          instructions: null,
          notes: null
        }))
      };
    },
    [lines, notes, selectedSiteId]
  );

  const transferToPharmacy = async (prescriptionId) => {
    if (!prescriptionId || !selectedSiteId) return null;
    const res = await medicalPrescriptionService.transferToPharmacy(prescriptionId, {
      site_id: selectedSiteId
    });
    if (!res.success) {
      throw new Error(res.message || 'Transmission pharmacie refusée.');
    }
    setPharmacyTransfer({
      transferred: true,
      dispensation_id: res.data?.dispensation_id,
      dispensation_number: res.data?.dispensation_number,
      status: res.data?.status || 'PENDING',
      site_id: res.data?.site_id,
      site_name: res.data?.site_name
    });
    return res.data;
  };

  const postPrescription = async (c) => {
    const prescriptionData = {
      ...buildPayload(c),
      transfer_to_pharmacy: true
    };
    if (activePrescriptionId) {
      const res = await medicalPrescriptionService.updatePrescription(activePrescriptionId, prescriptionData);
      if (!res.success) {
        throw new Error(res.message || 'Mise à jour refusée.');
      }
      return { ...res.data, pharmacy_transfer: res.pharmacy_transfer };
    }
    const res = await medicalPrescriptionService.createPrescription(prescriptionData);
    if (!res.success) {
      throw new Error(res.message || 'Création refusée.');
    }
    if (res.data?.id) {
      setActivePrescriptionId(res.data.id);
    }
    return { ...res.data, pharmacy_transfer: res.pharmacy_transfer };
  };

  const applyPharmacyTransferResult = (transfer) => {
    if (!transfer || transfer.error) return transfer?.error || null;
    setPharmacyTransfer({
      transferred: true,
      dispensation_id: transfer.dispensation_id,
      dispensation_number: transfer.dispensation_number,
      status: transfer.status || 'PENDING',
      site_id: transfer.site_id,
      site_name: transfer.site_name
    });
    return null;
  };

  const handlePrescriptionOnly = async () => {
    if (!consultation?.patient_name && !consultation?.id) return;
    if (effectiveReadOnly) {
      setError('Cette ordonnance n’est plus modifiable (pharmacie ou statut).');
      return;
    }
    setError('');
    setBusy(true);
    try {
      if (!selectedSiteId) {
        throw new Error('Sélectionnez un site de travail pour envoyer l’ordonnance à la pharmacie.');
      }
      const saved = await postPrescription(consultation);
      resetComposer();
      await loadSavedPrescriptions({ hydrateEditor: true });

      const transferErr = applyPharmacyTransferResult(saved?.pharmacy_transfer);
      if (transferErr) {
        setError(`Ordonnance enregistrée, mais envoi pharmacie impossible : ${transferErr}`);
      } else if (saved?.pharmacy_transfer?.dispensation_number) {
        setError('');
      } else if (saved?.id) {
        try {
          const manualErr = applyPharmacyTransferResult(await transferToPharmacy(saved.id));
          if (manualErr) {
            setError(`Ordonnance enregistrée, mais envoi pharmacie impossible : ${manualErr}`);
          }
        } catch (transferErr) {
          setError(
            transferErr.message ||
              'Ordonnance enregistrée, mais envoi à la pharmacie impossible.'
          );
        }
      }

      onPrescriptionSuccess?.();
    } catch (e) {
      setError(e.message || 'Enregistrement impossible.');
    } finally {
      setBusy(false);
    }
  };

  if (!consultation?.id && !focusId) return null;

  return (
    <div
      id="consultation-rx-inline-panel"
      className={`rx-inline-panel${effectiveReadOnly ? ' rx-inline-panel--readonly' : ''}`}
    >
      {activePrescriptionId ? (
        <p className="rx-inline-panel__doc-ref rx-doc-ref">
          {formatClinicalRefNumber(activePrescriptionId)}
        </p>
      ) : null}

      <div className="rx-inline-panel__summary">
        {pharmacyTransfer?.dispensation_number ? (
          <span className="rx-inline-panel__pharmacy-transfer">
            Pharmacie · {pharmacyTransfer.site_name || 'site'} · {pharmacyTransfer.dispensation_number}
            {pharmacyTransfer.status === 'PENDING' ? ' · en attente' : null}
            {pharmacyTransfer.status === 'VALIDATED' ? ' · validée' : null}
            {pharmacyTransfer.status === 'DISPENSED' ? ' · délivrée' : null}
          </span>
        ) : null}
        {hasDraftLines ? (
          <span className="rx-inline-panel__count">
            {lines.length} ligne{lines.length > 1 ? 's' : ''}
            {activePrescriptionId ? ' · enregistrée' : ''}
          </span>
        ) : (
          <span className="rx-inline-panel__hint">Ajoutez les médicaments un par un.</span>
        )}
      </div>

      {effectiveReadOnly && !readOnly ? (
        <div className="rx-inline-panel__alert rx-inline-panel__alert--info">
          Ordonnance verrouillée — déjà traitée en pharmacie ou annulée.
        </div>
      ) : null}

      {error ? <div className="rx-inline-panel__alert">{error}</div> : null}
      {stockLoadError ? <div className="rx-inline-panel__alert">{stockLoadError}</div> : null}

      {!effectiveReadOnly ? (
        <section className="rx-inline-panel__composer" aria-label="Ajouter un médicament">
          <h3 className="rx-inline-panel__composer-title">Ajouter un médicament</h3>

          <div className="rx-inline-panel__field mb-2">
            <label htmlFor="rx-med-search">Médicament</label>
            <RxMedicationSearch
              medications={medications}
              value={medInput}
              onChange={handleMedInputChange}
              onSelect={handleSelectMedication}
              selectedMed={selectedMed}
              stockByMedicationId={stockByMedicationId}
              stockSiteLabel={
                selectedSiteId
                  ? stockSiteLabel
                  : 'Sélectionnez un site de travail pour afficher le stock'
              }
              stockLoading={loadingStock}
              loading={loadingStock}
              disabled={busy || !selectedSiteId || loadingStock}
            />
          </div>

          <div className="rx-inline-panel__row rx-inline-panel__row--qty-poso">
            <div className="rx-inline-panel__field">
              <label htmlFor="rx-qty">{quantityLabel}</label>
              <input
                id="rx-qty"
                type="number"
                min={1}
                max={1000}
                className="rx-inline-panel__input"
                value={draft.quantity}
                onChange={(e) => setDraft((d) => ({ ...d, quantity: e.target.value }))}
                disabled={busy}
              />
            </div>
            <div className="rx-inline-panel__field">
              <label htmlFor="rx-poso">Posologie</label>
              <input
                id="rx-poso"
                type="text"
                className="rx-inline-panel__input"
                value={draft.dosage}
                onChange={(e) => setDraft((d) => ({ ...d, dosage: e.target.value }))}
                onKeyDown={handlePosoKeyDown}
                placeholder="Ex. 1 cp x 3 / j"
                disabled={busy}
              />
              <div className="rx-inline-panel__snippets">
                {POSOLOGY_SNIPPETS.map((snippet) => (
                  <button
                    key={snippet}
                    type="button"
                    className="rx-inline-panel__snippet"
                    disabled={busy}
                    onClick={() => applySnippet(snippet)}
                  >
                    {snippet}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <ClinicalButton
            variant="secondary"
            size="sm"
            block
            type="button"
            className="rx-inline-panel__add-btn"
            onClick={appendLine}
            disabled={busy}
          >
            + Ajouter à l&apos;ordonnance
          </ClinicalButton>
        </section>
      ) : null}

      {lines.length > 0 ? (
        <ol className="rx-inline-panel__lines">
          {lines.map((line, index) => {
            const qtyLine = formatQuantitySupply(line.quantity, line.unit_name);
            return (
              <li key={`${line.medication_id}-${index}`} className="rx-inline-panel__line">
                <span className="rx-inline-panel__line-index" aria-hidden="true">
                  {index + 1}.
                </span>
                <div className="rx-inline-panel__line-body">
                  <p className="rx-inline-panel__line-name">
                    {line.medication_name}
                    {qtyLine ? <span className="rx-inline-panel__line-qty"> · {qtyLine}</span> : null}
                  </p>
                  {line.dosage ? <p className="rx-inline-panel__line-poso">{line.dosage}</p> : null}
                </div>
                {!effectiveReadOnly ? (
                  <button
                    type="button"
                    className="rx-inline-panel__line-remove"
                    onClick={() => removeLine(index)}
                    disabled={busy}
                    aria-label={`Retirer ${line.medication_name}`}
                  >
                    <Icon name="times" size={16} />
                  </button>
                ) : null}
              </li>
            );
          })}
        </ol>
      ) : !loadingSaved ? (
        <p className="rx-inline-panel__empty">Aucun médicament.</p>
      ) : null}

      {!effectiveReadOnly ? (
        <div className="rx-inline-panel__notes mb-3">
          <label htmlFor="rx-notes">Notes (optionnel)</label>
          <textarea
            id="rx-notes"
            rows={2}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            disabled={busy}
            placeholder="Renouvellement, substitution…"
          />
        </div>
      ) : null}

      <ClinicalButtonStack>
        <ClinicalButton
          variant="view"
          icon="eye"
          block
          loading={loadingView}
          disabled={busy || loadingSaved || !canView}
          onClick={handleOpenView}
        >
          Voir &amp; imprimer
        </ClinicalButton>

        {!effectiveReadOnly ? (
          <ClinicalButton
            variant="primary"
            block
            loading={busy}
            disabled={busy || lines.length === 0}
            onClick={handlePrescriptionOnly}
          >
            {activePrescriptionId ? "Enregistrer l'ordonnance" : "Créer l'ordonnance"}
          </ClinicalButton>
        ) : null}
      </ClinicalButtonStack>

      <ConsultationPrescriptionViewModal
        show={showViewModal}
        onHide={() => setShowViewModal(false)}
        consultation={consultation}
        prescription={viewPrescription}
        previewUnsaved={viewPreviewUnsaved}
      />

      <RxPrescriptionStockWarningModal
        show={Boolean(stockWarningPrompt)}
        onHide={() => setStockWarningPrompt(null)}
        onConfirm={handleConfirmPrescribeDespiteStock}
        medicationName={stockWarningPrompt?.med?.name || ''}
        warning={stockWarningPrompt?.warning}
        busy={busy}
      />
    </div>
  );
}
